import type { ReactNode } from 'react';

/**
 * App icon.
 *
 * The catalogue has no artwork: winget manifests carry no icon and downloading
 * logos from publisher sites is neither reliable nor licensed. Instead the tile
 * is drawn from the app name: the initials plus a colour derived from a hash,
 * so an app keeps the same tile everywhere and neighbouring cards differ.
 */

const PALETTE = [
  '#4c8dff',
  '#7c5cff',
  '#e0559b',
  '#f2703f',
  '#e8a33d',
  '#3ecf8e',
  '#35b7c4',
  '#8b8f9a',
] as const;

/** FNV-1a: stable across runs, unlike `Math.random` or object ordering. */
function hash(value: string): number {
  let result = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 0x01000193) >>> 0;
  }
  return result;
}

export function tileColor(name: string): string {
  return PALETTE[hash(name) % PALETTE.length]!;
}

export function initials(name: string): string {
  // Words like "7-Zip" or "VLC media player" should read as "7Z" and "VM" only
  // when there is no better signal; single-word names use two letters.
  const words = name.split(/[\s\-_.]+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) {
    return words[0]!.slice(0, 2).toUpperCase();
  }
  return `${words[0]!.charAt(0)}${words[1]!.charAt(0)}`.toUpperCase();
}

export function AppIcon({
  src,
  name,
  size = 64,
}: {
  src: string | null;
  name: string;
  size?: number;
}) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        className="shrink-0 rounded-2xl border border-border bg-surface object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      className="flex shrink-0 items-center justify-center rounded-2xl font-semibold text-white"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.34,
        background: tileColor(name),
      }}
    >
      {initials(name)}
    </div>
  );
}

export function AppTile({
  children,
  as = 'div',
  href,
}: {
  children: ReactNode;
  as?: 'div' | 'a';
  href?: string;
}) {
  const className =
    'group flex gap-4 rounded-2xl border border-border bg-surface p-4 transition hover:border-muted hover:bg-surface-hover';
  if (as === 'a' && href) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }
  return <div className={className}>{children}</div>;
}
